import {
  useImperativeHandle,
  forwardRef,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { toast } from "sonner";
import { useTradeOperation } from "../hooks";
import { useMarkets } from "../hooks/useMarkets";
import { useTradePrice } from "../hooks/useTradePrice";
import { fromSatoshi } from "../sdk";
import {
  DEFAULT_BASE_URL,
  DEFAULT_WS_URL,
  DEFAULT_MNEMONIC,
  LEVERAGE_MIN,
  LEVERAGE_MAX,
  SIZE_PERCENT_MIN,
  SIZE_PERCENT_MAX,
  SIZE_PERCENT_STEP,
  PRICE_STEP,
  PRICE_MIN,
  CURRENCY_MIN_FRACTION_DIGITS,
  CURRENCY_MAX_FRACTION_DIGITS,
  SIZE_DISPLAY_DECIMALS,
  ORDER_TYPE_MARKET,
  ORDER_TYPE_LIMIT,
  MARGIN_MODE_CROSS,
  MARGIN_MODE_ISOLATED,
} from "./smartlayout/constants";
import "./smartlayout/layout.css";

export interface TradeOperationRef {
  leverage: number;
  setLeverage: (leverage: number) => void;
  calculateMaxRatio: (tempLeverage: number) => string;
}

interface TradeOperationProps {
  baseURL?: string;
  wsURL?: string;
  mnemonic?: string;
  marketId?: string;
  marketSymbol?: string; // Optional - will be auto-derived from selected market if not provided
  selectedBucketBalance?: number; // Balance from selected bucket (nominalValue)
  selectedBucketId?: string | null; // Bucket ID for display in label
  onLeverageClick?: () => void; // Callback when leverage badge is clicked
}

export interface TradeOperationComponent
  extends React.ForwardRefExoticComponent<
    TradeOperationProps & React.RefAttributes<TradeOperationRef>
  > {}

const TradeOperation = forwardRef<TradeOperationRef, TradeOperationProps>(
  function TradeOperation(
    {
      baseURL = DEFAULT_BASE_URL,
      wsURL = DEFAULT_WS_URL,
      mnemonic = DEFAULT_MNEMONIC,
      marketId,
      marketSymbol,
      selectedBucketBalance,
      selectedBucketId,
      onLeverageClick,
    },
    ref,
  ): JSX.Element {
    // Get selected market info from useMarkets hook
    const { selectedMarketId, selectedMarketSymbol } = useMarkets({
      baseURL,
      autoFetchMarkets: true,
    });

    // Determine the market ID to use (prop takes precedence, then selectedMarketId)
    const effectiveMarketId = marketId || selectedMarketId || "";

    // Derive market symbol: prop > selectedMarketSymbol > fallback
    const effectiveMarketSymbol =
      marketSymbol || selectedMarketSymbol || "Market";

    // Get trade price from useTradePrice hook for market orders
    const { lastPrice: tradePriceData } = useTradePrice({
      baseURL,
      wsURL,
      marketId: effectiveMarketId,
    });

    // Extract trade price for market orders
    const tradePrice = tradePriceData?.price || 0;

    const {
      orderType,
      setOrderType,
      fundPercent,
      sizeInput,
      price,
      leverage,
      positionMode,
      marginMode,
      availableBalance,
      currentPrice,
      isReady,
      loading,
      result,
      setFundPercent,
      setSizeInput,
      setPrice,
      setMarginMode,
      setLeverage,
      placeBuyOrder,
      placeSellOrder,
      calculateValue,
      calculateMargin,
      calculateSizeUSD,
      getOrderPrice,
      getAvailableTradeAmount,
    } = useTradeOperation({
      baseURL,
      mnemonic,
      marketId: effectiveMarketId,
      marketSymbol: effectiveMarketSymbol,
      selectedBucketBalance,
      tradePrice, // Pass trade price for market orders
    });

    // Memoize calculations from hook - depend on actual state values for proper updates
    const orderPrice = useMemo(
      () => getOrderPrice(),
      [getOrderPrice, orderType, price, currentPrice],
    );
    const availableTradeAmount = useMemo(
      () => getAvailableTradeAmount(),
      [getAvailableTradeAmount, availableBalance, leverage],
    );
    const value = useMemo(
      () => calculateValue(),
      [
        calculateValue,
        sizeInput,
        fundPercent,
        availableBalance,
        orderPrice,
        leverage,
      ],
    );
    const margin = useMemo(
      () => calculateMargin(),
      [calculateMargin, leverage, value],
    );

    // Convert value and margin from satoshi to decimal for display
    const valueDecimal = useMemo(() => {
      return fromSatoshi(value);
    }, [value]);

    const marginDecimal = useMemo(() => {
      return fromSatoshi(margin);
    }, [margin]);

    // Memoize currency formatting options
    const currencyFormatOptions = useMemo(
      () => ({
        minimumFractionDigits: CURRENCY_MIN_FRACTION_DIGITS,
        maximumFractionDigits: CURRENCY_MAX_FRACTION_DIGITS,
      }),
      [],
    );

    // Memoize current price display
    const currentPriceDisplay = useMemo(
      () => currentPrice.toFixed(CURRENCY_MIN_FRACTION_DIGITS),
      [currentPrice],
    );

    // Memoize available balance label
    const availableBalanceLabel = useMemo(
      () =>
        selectedBucketId
          ? `Available in ${selectedBucketId}`
          : "Available to Trade",
      [selectedBucketId],
    );

    // Memoize callbacks to prevent unnecessary re-renders
    const handleMarginModeToggle = useCallback(() => {
      setMarginMode(
        marginMode === MARGIN_MODE_CROSS
          ? MARGIN_MODE_ISOLATED
          : MARGIN_MODE_CROSS,
      );
    }, [marginMode, setMarginMode]);

    const handleOrderTypeMarket = useCallback(() => {
      setOrderType(ORDER_TYPE_MARKET);
    }, [setOrderType]);

    const handleOrderTypeLimit = useCallback(() => {
      setOrderType(ORDER_TYPE_LIMIT);
    }, [setOrderType]);

    const handleFundPercentIncrease = useCallback(() => {
      setFundPercent(
        Math.min(SIZE_PERCENT_MAX, fundPercent + SIZE_PERCENT_STEP),
      );
    }, [fundPercent, setFundPercent]);

    const handleFundPercentDecrease = useCallback(() => {
      setFundPercent(
        Math.max(SIZE_PERCENT_MIN, fundPercent - SIZE_PERCENT_STEP),
      );
    }, [fundPercent, setFundPercent]);

    // Use the hook's handleFundPercentChange directly - it already updates sizeInput
    const handleFundPercentChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const percent = parseInt(e.target.value);
        setFundPercent(percent); // This will update sizeInput via the hook
      },
      [setFundPercent],
    );

    const handlePriceChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setPrice(e.target.value);
      },
      [setPrice],
    );

    const handleSizeInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = parseFloat(e.target.value);
        setSizeInput(isNaN(val) ? 0 : val); // This will update fundPercent via the hook
      },
      [setSizeInput],
    );

    // Handle buy order with immediate toast notification
    const handleBuyOrder = useCallback(() => {
      toast.info("Submission of trade transaction", {
        description: "Processing your buy order...",
      });
      placeBuyOrder();
    }, [placeBuyOrder]);

    // Handle sell order with immediate toast notification
    const handleSellOrder = useCallback(() => {
      toast.info("Submission of trade transaction", {
        description: "Processing your sell order...",
      });
      placeSellOrder();
    }, [placeSellOrder]);

    // Expose leverage and setLeverage via ref
    useImperativeHandle(
      ref,
      () => ({
        leverage,
        setLeverage,
        calculateMaxRatio: (tempLeverage: number) => {
          const sizeUSD = calculateSizeUSD();
          const orderPriceValue = getOrderPrice();
          const orderValueAtLeverage = sizeUSD;
          return availableBalance > 0 && orderValueAtLeverage > 0
            ? (orderValueAtLeverage / availableBalance).toFixed(
                CURRENCY_MIN_FRACTION_DIGITS,
              )
            : tempLeverage.toFixed(CURRENCY_MIN_FRACTION_DIGITS);
        },
      }),
      [
        leverage,
        setLeverage,
        calculateSizeUSD,
        getOrderPrice,
        availableBalance,
      ],
    );

    // Show toast notifications when order result changes
    useEffect(() => {
      if (!result) return;

      if (result.success) {
        const description = result.orderId
          ? `Order ID: ${result.orderId}`
          : result.message || "Order placed successfully";
        toast.success("Order Placed Successfully", {
          description,
        });
      } else if (result.error) {
        toast.error("Order Failed", {
          description: result.error,
        });
      }
    }, [result]);

    return (
      <div className="component">
        <h2>{effectiveMarketSymbol}</h2>
        {/* Trade Configuration Panel */}
        <div className="trade-op-config">
          <div className="trade-op-badge">
            <span className="trade-op-badge-label">{positionMode}</span>
            <span className="trade-op-info-icon">i</span>
          </div>
          <div
            className="trade-op-badge"
            onClick={handleMarginModeToggle}
            style={{ cursor: "pointer" }}
          >
            <span className="trade-op-badge-label">{marginMode}</span>
            <span className="trade-op-info-icon">i</span>
          </div>
          <div
            className="trade-op-badge"
            onClick={onLeverageClick}
            style={{ cursor: "pointer" }}
          >
            <span className="trade-op-badge-label">{leverage}x</span>
            <span className="trade-op-info-icon">i</span>
          </div>
        </div>
        {/* Order Type Selection */}
        <div className="trade-op-order-type-selector">
          <button
            onClick={handleOrderTypeMarket}
            className={`btn-toggle ${orderType === ORDER_TYPE_MARKET ? "active" : ""}`}
          >
            MARKET
          </button>
          <button
            onClick={handleOrderTypeLimit}
            className={`btn-toggle ${orderType === ORDER_TYPE_LIMIT ? "active" : ""}`}
          >
            LIMIT
          </button>
        </div>
        {/* Limit Price Input (only for limit orders) */}
        {orderType === ORDER_TYPE_LIMIT && (
          <div className="trade-op-form-group">
            <div className="trade-op-form-row">
              <label htmlFor="price" className="trade-op-label">
                Price:
              </label>
              <input
                id="price"
                type="number"
                value={price}
                onChange={handlePriceChange}
                placeholder={currentPriceDisplay}
                step={PRICE_STEP}
                min={PRICE_MIN}
              />
            </div>
          </div>
        )}

        {/* Order Size Input and Slider */}
        <div className="trade-op-form-group">
          {/* Size controls - on a new row */}
          <div className="trade-op-form-row" style={{ marginBottom: "0.5rem" }}>
            <label htmlFor="size" className="trade-op-label">
              Size:
            </label>
            <div className="trade-op-size-controls">
              <input
                id="size"
                type="number"
                step="any"
                value={sizeInput === 0 ? "" : sizeInput}
                onChange={handleSizeInputChange}
                placeholder="0"
                min={PRICE_MIN}
                max={availableTradeAmount}
                className="trade-op-input"
              />
              <div className="trade-op-size-buttons">
                <button
                  onClick={handleFundPercentIncrease}
                  className="btn-small"
                >
                  ▲
                </button>
                <button
                  onClick={handleFundPercentDecrease}
                  className="btn-small"
                >
                  ▼
                </button>
              </div>
            </div>
          </div>
          <input
            type="range"
            min={SIZE_PERCENT_MIN}
            max={SIZE_PERCENT_MAX}
            value={fundPercent}
            onChange={handleFundPercentChange}
            step={SIZE_PERCENT_STEP}
            style={{ width: "100%" }}
          />
          <div className="trade-op-slider-labels">
            <span>0</span>
            <span>20</span>
            <span>40</span>
            <span>60</span>
            <span>80</span>
            <span>100</span>
          </div>
        </div>

        {/* Buy/Sell Action Panels */}
        <div className="trade-op-action-panel">
          {/* Buy Panel */}
          <div>
            <div className="trade-op-panel-title buy">BUY</div>
            <div className="trade-op-panel-size">
              BTC {sizeInput.toFixed(3)}/100.000
            </div>
            <button
              onClick={handleBuyOrder}
              disabled={loading || !isReady || value <= 0}
              className="btn-buy"
            >
              {loading ? "PLACING..." : "BUY LONG"}
            </button>
            <div className="trade-op-panel-details">
              <div>Value {valueDecimal.toFixed(2)}</div>
              <div>Margin {marginDecimal.toFixed(2)}</div>
            </div>
          </div>

          {/* Sell Panel */}
          <div>
            <div className="trade-op-panel-title sell">SELL</div>
            <div className="trade-op-panel-size">
              {sizeInput.toFixed(3)}/100.000 BTC
            </div>
            <button
              onClick={handleSellOrder}
              disabled={loading || !isReady || value <= 0}
              className="btn-sell"
            >
              {loading ? "PLACING..." : "SELL SHORT"}
            </button>
            <div className="trade-op-panel-details">
              <div>
                Value {valueDecimal.toFixed(CURRENCY_MIN_FRACTION_DIGITS)}
              </div>
              <div>
                Margin {marginDecimal.toFixed(CURRENCY_MIN_FRACTION_DIGITS)}
              </div>
            </div>
          </div>
        </div>
        {/* Available Funds - on its own row */}
        <div className="trade-op-info-panel">
          <div>
            <label>{availableBalanceLabel}</label>
            <div className="trade-op-available-funds">
              {availableTradeAmount.toLocaleString(
                "en-US",
                currencyFormatOptions,
              )}{" "}
              USDC
            </div>
          </div>
        </div>
      </div>
    );
  },
);

export default TradeOperation;
